import { QueryFunctionContext } from '@tanstack/react-query'
import { DELETE, GET, POST, PUT } from '../../../redux/middleware/types'
import voucherEndpoints from '../api/voucherEndpoints'
import {
  Voucher,
  VoucherQueryParams,
  VoucherQueryResult,
  VoucherType,
  TransactionQueryResult
} from '../../../pages/reporting/vouchers/types'
import voucherQueryKeys from '../queries/voucherQueryKeys'
import integrationRequest from '../../../services/api/integrationRequest'
import { Company } from '../../../types/company/Company'
import { base64ToArrayBuffer } from './helpers'
import { DeleteVoucherAttachmentParams, GetVoucherAttachmentParams } from '../types/voucherService'

export const getVouchers = async ({
  queryKey: [{ params, companyId, dimensions }],
  signal
}: QueryFunctionContext<ReturnType<(typeof voucherQueryKeys)['list']>>): Promise<Partial<VoucherQueryResult>[]> => {
  if (!companyId || companyId === 'undefined') return []
  return integrationRequest<Partial<VoucherQueryResult>[]>({
    method: GET,
    url: voucherEndpoints.list(),
    signal,
    params: {
      params,
      companyId,
      dimensions
    }
  })
}

export const getVoucherTypes = async ({
  queryKey: [{ params, companyId }],
  signal
}: QueryFunctionContext<ReturnType<(typeof voucherQueryKeys)['type']>>): Promise<VoucherType[]> => {
  if (!companyId || companyId === 'undefined') return []
  return integrationRequest<VoucherType[]>({
    method: GET,
    url: voucherEndpoints.types(),
    signal,
    params: {
      ...params,
      companyId
    }
  })
}

export const getVoucherXlsx = async (
  requestParams?: Partial<VoucherQueryParams>,
  companyId?: Company['id'],
  dimensions?: string
) => {
  try {
    const response = await integrationRequest<string>({
      method: GET,
      url: voucherEndpoints.xlsx(),
      params: {
        params: requestParams,
        companyId,
        dimensions
      }
    })

    const arrayBuffer = base64ToArrayBuffer(response)
    return new Blob([arrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
  } catch (error) {
    console.error('Failed to download voucher XLSX:', error)
    throw new Error('Failed to process voucher export')
  }
}

export const fetchTransactions = async (
  requestParams: Partial<VoucherQueryParams>,
  companyId: Company['id'],
  dimensions?: string
): Promise<TransactionQueryResult> => {
  if (!companyId || companyId === 'undefined') {
    return { vouchers: [], page: 1, pages: 0, results: 0 }
  }
  return integrationRequest<TransactionQueryResult>({
    method: GET,
    url: voucherEndpoints.list(),
    params: {
      params: { ...requestParams, groupBy: 'transaction' },
      companyId,
      dimensions
    }
  })
}

export const createVoucher = async (data: FormData, companyId: string): Promise<Voucher> => {
  return integrationRequest<Voucher>({
    method: POST,
    url: voucherEndpoints.accounting(),
    data,
    params: {
      companyId
    }
  })
}

export const updateVoucher = async (data: FormData, voucherId: Voucher['id'], companyId: string): Promise<Voucher> => {
  return integrationRequest<Voucher>({
    method: PUT,
    url: voucherEndpoints.accountingDetail(voucherId),
    data,
    params: {
      companyId
    }
  })
}

export const deleteVoucher = async (companyId: string, voucherId: Voucher['id']): Promise<Voucher> => {
  return integrationRequest<Voucher>({
    method: DELETE,
    url: voucherEndpoints.accountingDetail(voucherId),
    params: {
      companyId
    }
  })
}

export const deleteVoucherAttachment = ({
  voucherId,
  attachmentId,
  companyId,
  signal
}: DeleteVoucherAttachmentParams) => {
  return integrationRequest({
    method: DELETE,
    url: voucherEndpoints.deleteAttachment(voucherId, attachmentId),
    params: { companyId },
    signal,
    responseType: 'arraybuffer'
  })
}

export const getVoucherAttachment = async ({
  voucherId,
  attachmentId,
  companyId,
  signal
}: GetVoucherAttachmentParams): Promise<ArrayBuffer> => {
  return integrationRequest({
    method: GET,
    url: voucherEndpoints.getAttachment(voucherId, attachmentId),
    params: { companyId },
    signal,
    responseType: 'arraybuffer'
  })
}

export const fetchVouchers = async (
  requestParams: Partial<VoucherQueryParams>,
  companyId: Company['id'],
  dimensions?: string
): Promise<VoucherQueryResult> => {
  if (!companyId || companyId === 'undefined') {
    return { vouchers: [], page: 1, pages: 0, results: 0 }
  }
  return integrationRequest<VoucherQueryResult>({
    method: GET,
    url: voucherEndpoints.list(),
    params: {
      params: requestParams,
      companyId,
      dimensions
    }
  })
}
